import { Page, Text, View, Image, Document, Link } from "@react-pdf/renderer";
import { styles } from "./styles";

type Props = {
  totalSavings: number;
  totalRisk: number;
  contractChanges: number;
  wrongSupplier: number;
  potentialDelays: number;
  potentialClaims: number;
  processTotal: number;
  processEfficiency: number;
  directCosts: number;
  externalConsultancy: number;
  environmentalExpected: number;
  paperSavings: number;
  petrolCosts: number;
  treesSaved: number;
  chartImage: string | null;
  riskPercentage: number;
  processPercentage: number;
  environmentalPercentage: number;
};

const formatNumber = (number: number) =>
  new Intl.NumberFormat("en-UK", {
    style: "currency",
    currency: "GBP",
    maximumSignificantDigits: 9,
  }).format(number);

const getYear = () => {
  const date = new Date();
  return date.getFullYear();
};

export default function PdfDownload({
  totalSavings,
  totalRisk,
  contractChanges,
  wrongSupplier,
  potentialDelays,
  potentialClaims,
  processTotal,
  processEfficiency,
  directCosts,
  externalConsultancy,
  environmentalExpected,
  paperSavings,
  petrolCosts,
  treesSaved,
  chartImage,
  riskPercentage,
  processPercentage,
  environmentalPercentage,
}: Props) {
  return (
    <Document>
      <Page style={styles.page}>
        <View style={styles.topContainer}>
          <Image src="/images/pdf/phone.png" style={styles.phone} />
          <View>
            <View style={styles.logoContainer}>
              <Image src="/images/pdf/commerce_logo.png" style={styles.logo} />
              <Image src="/images/pdf/award_logo.png" style={styles.logo} />
            </View>
            <View style={styles.headingContainer}>
              <Text
                style={{
                  margin: "auto",
                  width: 180,
                  fontSize: 14,
                }}
              >
                RETURN ON INVESTMENT
              </Text>
              <Text
                style={{
                  fontSize: 32,
                  width: 210,
                  margin: "auto",
                }}
              >
                ROI Calculator
              </Text>
              <Text style={styles.yourReport}>YOUR REPORT</Text>
            </View>
          </View>
        </View>

        <View style={{ flexDirection: "row" }}>
          <View style={styles.totalResultContainer}>
            <Image
              style={styles.totalResultImg}
              src="/images/pdf/total_result.png"
            />
            <Text style={styles.resultText}>{formatNumber(totalSavings)}</Text>
          </View>

          <View style={styles.totalHeadingContainer}>
            <Image
              style={styles.totalHeadingImg}
              src="/images/pdf/total_heading.png"
            />
            <View style={styles.headingText}>
              <Text style={{ marginBottom: 6 }}>Your total estimated</Text>
              <Text style={{ paddingLeft: 30 }}>annual benefit is</Text>
            </View>
          </View>
        </View>

        <View
          style={{
            flexDirection: "row",
            marginTop: 25,
            marginHorizontal: "auto",
            gap: 20,
          }}
        >
          <View style={styles.arrowsContainer}>
            <Image style={styles.arrow} src="/images/pdf/risk_arrow.png" />
            <Text style={styles.riskText}>Risk Savings</Text>
            <Image style={styles.arrow} src="/images/pdf/process_arrow.png" />
            <Text style={styles.processText}>Process Savings</Text>
            <Image
              style={styles.arrow}
              src="/images/pdf/environment_arrow.png"
            />
            <Text style={styles.environmentalText}>Environmental Savings</Text>
          </View>

          <View style={styles.percentContainer}>
            <Image style={styles.percent} src="/images/pdf/risk_percent.png" />
            <Text style={styles.riskPercent}>{riskPercentage}%</Text>
            <Image
              style={styles.percent}
              src="/images/pdf/process_percent.png"
            />
            <Text style={styles.processPercent}>{processPercentage}%</Text>
            <Image
              style={styles.percent}
              src="/images/pdf/environment_percent.png"
            />
            <Text style={styles.environmentPercent}>
              {environmentalPercentage}%
            </Text>
          </View>
          <Image style={styles.pieChart} src={chartImage ?? ""} />
        </View>

        <View>
          <View style={styles.description}>
            <View style={styles.iconContainer}>
              <Image src="/images/pdf/risk_icon.png" />
            </View>
            <View
              style={[styles.textContainer, { backgroundColor: "#9b258f" }]}
            >
              <Text style={{ fontSize: 16 }}>
                Risk Total Expected: {formatNumber(totalRisk)}
              </Text>
              <Text>Contract changes: {formatNumber(contractChanges)}</Text>
              <Text>
                Choosing wrong supplier: {formatNumber(wrongSupplier)}
              </Text>
              <Text>
                Potential delays caused by challenges:{" "}
                {formatNumber(potentialDelays)}
              </Text>
              <Text>
                Potential successful claims: {formatNumber(potentialClaims)}
              </Text>
            </View>
          </View>

          <View style={styles.description}>
            <View style={styles.iconContainer}>
              <Image src="/images/pdf/process_icon.png" />
            </View>
            <View
              style={[styles.textContainer, { backgroundColor: "#ee0f64" }]}
            >
              <Text style={{ fontSize: 16 }}>
                Process Total Expected: {formatNumber(processTotal)}
              </Text>
              <Text>
                Process efficiency savings: {formatNumber(processEfficiency)}
              </Text>
              <Text>Direct costs: {formatNumber(directCosts)}</Text>
              <Text>
                External consultancy: {formatNumber(externalConsultancy)}
              </Text>
            </View>
          </View>

          <View style={styles.description}>
            <View style={styles.iconContainer}>
              <Image src="/images/pdf/environmental_icon.png" />
            </View>
            <View
              style={[styles.textContainer, { backgroundColor: "#098f4e" }]}
            >
              <Text style={{ fontSize: 16 }}>
                Environmental Total Expected:{" "}
                {formatNumber(environmentalExpected)}
              </Text>
              <Text>Cost of paper savings: {formatNumber(paperSavings)}</Text>
              <Text>Petrol costs saved: {formatNumber(petrolCosts)}</Text>
              <Text>Number of trees saved: {treesSaved}</Text>
            </View>
          </View>
        </View>

        <View style={styles.footer}>
          <Text>Copyright © {getYear()} Commerce Decisions Ltd</Text>
          <Text>&#8226;</Text>
          <Link
            src="mailto:info@commercedecisions.com"
            style={styles.footerLink}
          >
            info@commercedecisions.com
          </Link>
          <Text>&#8226;</Text>
          <Link src="https://commercedecisions.com/" style={styles.footerLink}>
            www.commercedecisions.com
          </Link>
        </View>
      </Page>
    </Document>
  );
}
